// Lead capture — contact/enrollment messages and the double opt-in bulletin
// subscription. All submissions go to the live web-api: server-side persistence,
// email acknowledgement, the double opt-in bulletin flow, and the Altcha
// proof-of-work guard.
import axios from 'axios';
import { api } from '../lib/api-client';
import { solveAltcha } from '../lib/altcha';
import type {
  ContactMessageDto,
  BulletinSubscriptionResult,
  CreateGroupEnrollmentDto,
  GroupEnrollmentResult,
} from '@munifocus/contracts-web';

/**
 * True when a submission was rejected by the Altcha proof-of-work guard (HTTP
 * 403), so forms can show a "please refresh and retry" message distinct from a
 * generic send failure.
 */
export function isVerificationError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 403;
}

/** Normalise an i18next language tag (e.g. `fr-CA`) to the API's `en | fr`. */
export function apiLang(language: string | undefined): 'en' | 'fr' {
  return language?.toLowerCase().startsWith('fr') ? 'fr' : 'en';
}

/**
 * Submit a contact / lead message. Resolves on success, throws otherwise.
 *
 * Training enrollments go through here too: the site posts a `training_register`
 * request carrying the chosen `sessionSlug`, which an admin approves into an
 * enrollment from the Requests inbox.
 */
export async function submitContact(input: ContactMessageDto): Promise<void> {
  // Solve the Altcha proof-of-work first and attach it so the server-side guard
  // accepts the submission (fail-open when the server has no ALTCHA_HMAC_KEY).
  const solution = await solveAltcha();
  await api.post('/contact', input, { headers: { 'x-altcha-solution': solution } });
}

/**
 * Submit a group / organization registration for one session.
 *
 * Unlike {@link submitContact}, this creates the seats directly (in `pending`,
 * still awaiting back-office confirmation) and returns a per-attendee outcome —
 * a group of five against three free seats has no single answer, so the caller
 * must render the breakdown rather than a generic success message.
 *
 * Same Altcha proof-of-work as the other public forms.
 */
export async function submitGroupRegistration(
  slug: string,
  input: CreateGroupEnrollmentDto,
): Promise<GroupEnrollmentResult> {
  const solution = await solveAltcha();
  const { data } = await api.post<GroupEnrollmentResult>(
    `/training-sessions/${encodeURIComponent(slug)}/group-registration`,
    input,
    { headers: { 'x-altcha-solution': solution } },
  );
  return data;
}

/**
 * Begin a bulletin subscription. This is a double opt-in: a confirmation email
 * is sent and the result is `status: 'pending'` until the visitor confirms.
 */
export async function subscribeBulletin(
  email: string,
  language: 'en' | 'fr',
): Promise<BulletinSubscriptionResult> {
  const { data } = await api.post<BulletinSubscriptionResult>('/bulletin-subscription/subscribe', {
    email,
    language,
  });
  return data;
}

/** Confirm a subscription from the emailed token (double opt-in). */
export async function confirmBulletinSubscription(
  token: string,
): Promise<BulletinSubscriptionResult> {
  const { data } = await api.post<BulletinSubscriptionResult>('/bulletin-subscription/confirm', {
    token,
  });
  return data;
}

/** Opt out of the bulletin using the one-click token from an email. */
export async function unsubscribeBulletin(token: string): Promise<BulletinSubscriptionResult> {
  const { data } = await api.post<BulletinSubscriptionResult>(
    '/bulletin-subscription/unsubscribe',
    { token },
  );
  return data;
}

/** One of the 77 New Brunswick local governments, for the group booking picker. */
export interface PublicLocalGovernment {
  id: string;
  name: string;
  nameFr: string;
}

/**
 * The local-government list a group booker chooses from.
 *
 * Asking here is what keeps one municipality one payer: free text alone made
 * "Town of Rothesay", "Rothesay" and "rothesay" three separate ledgers, and the
 * only moment that can be fixed is at booking time.
 */
export async function listPublicLocalGovernments(): Promise<PublicLocalGovernment[]> {
  const { data } = await api.get<PublicLocalGovernment[]>('/local-governments');
  return data;
}
